/**
 * @fileoverview Analysis API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Analysis module.
 * Handles AI-powered learning type analysis with GPT-4/GPT-5.
 * Supports pre-AI and post-AI human review workflow.
 */

import { z } from 'zod';
import { createContract, ApiSuccessResponseSchema } from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Analysis Type Enum
 */
export enum AnalysisType {
	GENERAL = 'general',
	THERAPIST = 'therapist',
	TEACHER = 'teacher',
	DETAILED = 'detailed',
	RECOMMENDATIONS = 'recommendations',
	// New Types (V2 Analysis Set)
	INTRO = 'intro',
	TEACHER_V2 = 'teacher_v2',
	LEARNING_PROFILE = 'learning_profile',
	CHILD = 'child',
}

/**
 * Analysis Status Enum
 */
export enum AnalysisStatus {
	PENDING = 'pending',
	GENERATING = 'generating',
	GENERATED = 'generated',
	REVIEW_PENDING = 'review_pending',
	APPROVED = 'approved',
	REJECTED = 'rejected',
	FAILED = 'failed',
}

/**
 * Template Type Enum
 */
export enum TemplateType {
	GENERAL_ANALYSIS = 'general_analysis',
	DETAILED_ANALYSIS = 'detailed_analysis',
	TEACHER_RECOMMENDATIONS = 'teacher_recommendations',
	TEACHER_PERSPECTIVE = 'teacher_perspective',
	THERAPIST_PERSPECTIVE = 'therapist_perspective',
	// New Types
	INTRO_ANALYSIS = 'intro_analysis',
	TEACHER_PERSPECTIVE_V2 = 'teacher_perspective_v2',
	LEARNING_PROFILE = 'learning_profile',
	CHILD_PERSPECTIVE = 'child_perspective',
}

/**
 * Analysis Set Name Enum
 * Predefined sets of analysis types for convenient generation
 */
export enum AnalysisSetName {
	/** New default: INTRO, TEACHER_V2, LEARNING_PROFILE */
	STANDARD = 'standard',
	/** Legacy 5-type set: GENERAL, THERAPIST, TEACHER, DETAILED, RECOMMENDATIONS */
	LEGACY = 'legacy',
}

/**
 * Analysis Set Registry
 * Maps set names to their corresponding analysis types
 */
export const ANALYSIS_SET_REGISTRY: Record<AnalysisSetName, AnalysisType[]> = {
	[AnalysisSetName.STANDARD]: [
		AnalysisType.INTRO,
		AnalysisType.TEACHER_V2,
		AnalysisType.LEARNING_PROFILE,
		AnalysisType.CHILD,
	],
	[AnalysisSetName.LEGACY]: [
		AnalysisType.GENERAL,
		AnalysisType.THERAPIST,
		AnalysisType.TEACHER,
		AnalysisType.DETAILED,
		AnalysisType.RECOMMENDATIONS,
	],
} as const;

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Analysis Type Schema
 */
export const AnalysisTypeSchema = z.enum(AnalysisType);

/**
 * Analysis Status Schema
 */
export const AnalysisStatusSchema = z.enum(AnalysisStatus);

/**
 * Template Type Schema
 */
export const TemplateTypeSchema = z.enum(TemplateType);

/**
 * Single Analysis Result Schema
 */
export const AnalysisResultSchema = z.object({
	testId: z.uuid(),
	analysisType: AnalysisTypeSchema,
	content: z.string(),
	generatedAt: z.iso.datetime(),
	isAiGenerated: z.boolean(),
	model: z.string().optional(), // e.g., "gpt-4", "gpt-5"
	tokensUsed: z.number().int().nonnegative().optional(),
});
export type AnalysisResult = z.infer<typeof AnalysisResultSchema>;

/**
 * Comprehensive Analysis Schema
 */
export const ComprehensiveAnalysisSchema = z.object({
	testId: z.uuid(),
	general: z.string(),
	therapistTips: z.string(),
	teacherPerspective: z.string(),
	detailedAnalysis: z.string(),
	recommendations: z.string(),
	generatedAt: z.iso.datetime(),
	isAiGenerated: z.boolean(),
	model: z.string().optional(),
	totalTokensUsed: z.number().int().nonnegative().optional(),
});
export type ComprehensiveAnalysis = z.infer<typeof ComprehensiveAnalysisSchema>;

/**
 * Comprehensive Analysis V2 Schema (New Analysis Set)
 * Contains the 3 new analysis types: INTRO, TEACHER_V2, LEARNING_PROFILE
 */
export const ComprehensiveAnalysisV2Schema = z.object({
	testId: z.uuid(),
	intro: z.string(),
	teacherPerspectiveV2: z.string(),
	learningProfile: z.string(),
	childPerspective: z.string(),
	generatedAt: z.iso.datetime(),
	isAiGenerated: z.boolean(),
	model: z.string().optional(),
	totalTokensUsed: z.number().int().nonnegative().optional(),
});
export type ComprehensiveAnalysisV2 = z.infer<typeof ComprehensiveAnalysisV2Schema>;

/**
 * Unified Comprehensive Analysis Response (supports both sets)
 */
export const ComprehensiveAnalysisResponseSchema = z.union([
	ComprehensiveAnalysisSchema,
	ComprehensiveAnalysisV2Schema,
]);
export type ComprehensiveAnalysisResponse = z.infer<typeof ComprehensiveAnalysisResponseSchema>;

/**
 * Generate Single Analysis Request Schema
 */
export const GenerateSingleAnalysisRequestSchema = z.object({
	testId: z.uuid(),
	analysisType: AnalysisTypeSchema,
	useMock: z.boolean().optional(),
});
export type GenerateSingleAnalysisRequest = z.input<typeof GenerateSingleAnalysisRequestSchema>;

/**
 * Generate Comprehensive Analysis Request Schema
 * Uses named sets for convenient generation of predefined analysis combinations
 */
export const GenerateComprehensiveAnalysisRequestSchema = z.object({
	testId: z.uuid(),
	useMock: z.boolean().optional(),
	/** Analysis set to generate. Default: 'standard' (INTRO, TEACHER_V2, LEARNING_PROFILE) */
	setName: z.nativeEnum(AnalysisSetName).optional().default(AnalysisSetName.STANDARD),
});
export type GenerateComprehensiveAnalysisRequest = z.input<
	typeof GenerateComprehensiveAnalysisRequestSchema
>;

/**
 * Analysis Entity Schema (Database)

 * and ISO strings (from API). This allows @ZodResponse to work correctly with DB entities.
 */
export const AnalysisEntitySchema = z.object({
	id: z.uuid(),
	testResultId: z.uuid(),
	analysisType: AnalysisTypeSchema,
	content: z.string(),
	status: AnalysisStatusSchema,
	isAiGenerated: z.boolean(),
	model: z.string().optional(),
	tokensUsed: z.number().int().nonnegative().optional(),
	prompt: z.string().nullable().optional(), // The prompt sent to AI for this analysis
	generatedAt: z.iso.datetime(),
	reviewedAt: z.iso.datetime().nullable().optional(), // ✅ FIX: Allow null values from DB
	reviewedBy: z.string().nullable().optional(), // ✅ FIX: Allow null values from DB
	reviewNotes: z.string().nullable().optional(), // ✅ FIX: Allow null values from DB
	metadata: z.record(z.string(), z.unknown()).nullable().optional(), // ✅ FIX: Allow null values from DB
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type AnalysisEntity = z.infer<typeof AnalysisEntitySchema>;

/**
 * Template Variables Schema
 * Includes all data for AI analysis: basic info, personalization, and learning scores
 */
export const TemplateVariablesSchema = z.object({
	// Basic information
	child_name: z.string(),
	child_age: z.string(),
	child_school: z.string().optional(),
	child_situation: z.string().optional(),
	// Personalization data (NEW)
	child_interests: z.string().optional(),
	child_gender: z.string().optional(),
	challenges: z.string().optional(),
	parent_feeling: z.string().optional(),
	selected_goals: z.string().optional(),
	// Learning style scores
	visuell: z.string(),
	auditiv: z.string(),
	lese: z.string(),
	kinaesthetisch: z.string(),
	integriert: z.string(),
	hochbegabung: z.string(),
	auditive_wahrnehmung: z.string(),
	hochsensibilität: z.string(),
	adhs: z.string(),
	// Questionnaire data (for child-facing analysis)
	questions_with_answers: z.string().optional(),
});
export type TemplateVariables = z.infer<typeof TemplateVariablesSchema>;

/**
 * Get Analysis Request Schema
 */
export const GetAnalysisRequestSchema = z.object({
	testId: z.uuid(),
	analysisType: AnalysisTypeSchema.optional(),
});
export type GetAnalysisRequest = z.infer<typeof GetAnalysisRequestSchema>;

/**
 * Get Analysis Response Schema
 */
export const GetAnalysisResponseSchema = z.object({
	analyses: z.array(AnalysisEntitySchema),
	comprehensive: ComprehensiveAnalysisSchema.optional(),
});
export type GetAnalysisResponse = z.infer<typeof GetAnalysisResponseSchema>;

/**
 * Regenerate Analysis Request Schema
 */
export const RegenerateAnalysisRequestSchema = z.object({
	analysisId: z.uuid(),
	useMock: z.boolean().optional(),
});
export type RegenerateAnalysisRequest = z.infer<typeof RegenerateAnalysisRequestSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

/**
 * Single Analysis API Response
 * { success: true, data: AnalysisResult, timestamp: string }
 */
export const AnalysisResultApiResponseSchema = ApiSuccessResponseSchema(AnalysisResultSchema);

/**
 * Comprehensive Analysis API Response (supports both standard and V2)
 * { success: true, data: ComprehensiveAnalysis | ComprehensiveAnalysisV2, timestamp: string }
 */
export const ComprehensiveAnalysisApiResponseSchema = ApiSuccessResponseSchema(
	ComprehensiveAnalysisResponseSchema,
);

/**
 * Comprehensive Analysis V2 API Response
 * { success: true, data: ComprehensiveAnalysisV2, timestamp: string }
 */
export const ComprehensiveAnalysisV2ApiResponseSchema = ApiSuccessResponseSchema(
	ComprehensiveAnalysisV2Schema,
);

/**
 * Get Analysis API Response
 * { success: true, data: GetAnalysisResponse, timestamp: string }
 */
export const GetAnalysisApiResponseSchema = ApiSuccessResponseSchema(GetAnalysisResponseSchema);

/**
 * Delete Analysis API Response
 * { success: true, data: { success: boolean, message: string }, timestamp: string }
 */
const DeleteAnalysisDataSchema = z.object({ success: z.boolean(), message: z.string() });
export const DeleteAnalysisApiResponseSchema = ApiSuccessResponseSchema(DeleteAnalysisDataSchema);

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Analysis API Contract
 *
 * Defines the complete API surface for the Analysis module.
 * Both frontend and backend use this as the single source of truth.
 */
export const ANALYSIS_API_CONTRACT = createContract('/analysis', {
	/**
	 * POST /analysis/generate-single
	 * Generate a single analysis (e.g., general, therapist, teacher)
	 */
	generateSingle: {
		method: 'POST' as const,
		path: '/generate-single',
		body: GenerateSingleAnalysisRequestSchema,
		response: AnalysisResultSchema,
	},

	/**
	 * POST /analysis/generate-comprehensive
	 * Generate comprehensive analysis using named sets
	 * Default: 'standard' (INTRO, TEACHER_V2, LEARNING_PROFILE)
	 * Legacy: 'legacy' (GENERAL, THERAPIST, TEACHER, DETAILED, RECOMMENDATIONS)
	 */
	generateComprehensive: {
		method: 'POST' as const,
		path: '/generate-comprehensive',
		body: GenerateComprehensiveAnalysisRequestSchema,
		response: ComprehensiveAnalysisResponseSchema,
	},


	/**
	 * GET /analysis/:testId
	 * Get all analyses for a test result
	 */
	getByTestId: {
		method: 'GET' as const,
		path: '/:testId',
		params: z.object({
			testId: z.uuid(),
		}),
		query: z
			.object({
				analysisType: AnalysisTypeSchema.optional(),
			})
			.optional(),
		response: GetAnalysisResponseSchema,
	},

	/**
	 * POST /analysis/:analysisId/regenerate
	 * Regenerate a specific analysis
	 */
	regenerate: {
		method: 'POST' as const,
		path: '/:analysisId/regenerate',
		params: z.object({
			analysisId: z.uuid(),
		}),
		body: z
			.object({
				useMock: z.boolean().optional(),
			})
			.optional(),
		response: AnalysisResultSchema,
	},

	/**
	 * DELETE /analysis/:analysisId
	 * Delete a specific analysis
	 */
	delete: {
		method: 'DELETE' as const,
		path: '/:analysisId',
		params: z.object({
			analysisId: z.uuid(),
		}),
		response: z.object({
			success: z.boolean(),
			message: z.string(),
		}),
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const ANALYSIS_ROUTES = {
	base: ANALYSIS_API_CONTRACT.base,
	generateSingle: ANALYSIS_API_CONTRACT.routes.generateSingle.path,
	generateComprehensive: ANALYSIS_API_CONTRACT.routes.generateComprehensive.path,
	getByTestId: ANALYSIS_API_CONTRACT.routes.getByTestId.path,
	regenerate: ANALYSIS_API_CONTRACT.routes.regenerate.path,
	delete: ANALYSIS_API_CONTRACT.routes.delete.path,
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type AnalysisApiContract = typeof ANALYSIS_API_CONTRACT;
